import authFetch from './fetch';
import qs from 'qs';
import { getQueryParams, makeRequest } from './utils';
import { useQuery } from '@tanstack/react-query';
import { CacheConfig } from './consts';

const DRIVERS_PATH = '/api/Drivers';

export const routes = {
  // Drivers
  safetyDriversGridPath: (params) => `/api/Drivers/Grid${getQueryParams(params)}`,
  driversLookupPath: (params) => `/api/Drivers/lookup${getQueryParams(params)}`,
  driversPath: () => '/api/Drivers',
  driversExportPath: (params) => `/api/Drivers/Export${getQueryParams(params)}`,
  // Driver Details
  driverNotesPathByDriverId: (driverId, params) => `/api/Drivers/${driverId}/notes${getQueryParams(params)}`,
  driverNotesPinTogglePath: (driverId, noteId) => `/api/Drivers/${driverId}/notes/${noteId}/pin`,
  driverStatsPath: (driverId, params) => `/api/drivers/${driverId}/stats${getQueryParams(params)}`,
  driverFilesPathByDriverId: (driverId, params) => `/api/Drivers/${driverId}/Files${getQueryParams(params)}`,
  driverFilePath: (driverId, fileId) => `/api/Drivers/${driverId}/Files/${fileId}`,
  // Bulk works
  changeAllAssign: () => '/api/Drivers/changeall/assign',
  changeAllValidate: () => '/api/Drivers/changeall/validate',
  bulkDqfDownload: () => '/api/Drivers/DQF',
  dqfDownload: (driverId) => `/api/Drivers/${driverId}/Files/DQF`,
  // Driver Pay
  driverBanksPath: (id) => `/api/drivers/${id}/bankAccounts`,
  driverAccountInfoPath: (driverId, accountId) => `/api/drivers/${driverId}/bankAccounts/${accountId}`,
  // Sub Elements
  correctiveActionsPathByDriverId: (driverId) => `/api/drivers/${driverId}/correctiveActions`,
  correctiveActionPathById: (driverId, id) => `/api/Drivers/${driverId}/correctiveActions/${id}`,
  driverInsuranceEmailPathByDriverId: (driverId) => `/api/Drivers/${driverId}/insuranceEmail`,
  driverInsurancePathByDriverId: (driverId) => `/api/Drivers/${driverId}/insurance`,
  driverInsurancePathByIds: (driverId, insruanceId) => `/api/Drivers/${driverId}/insurance/${insruanceId}`,
  driverPastInsurancesPathByIds: (driverId, insruanceId, params) => (
    `/api/Drivers/${driverId}/insurance/types/${insruanceId}/expired${getQueryParams(params)}`),
  driverFutureInsurancesPathByIds: (driverId, insruanceId) => (
    `/api/Drivers/${driverId}/insurance/types/${insruanceId}/future`),
  driverFreeWeeksPathGridById: (driverId, insuranceTypeId, params) => (
    `/api/Drivers/${driverId}/insurance/types/${insuranceTypeId}/freeWeeks${getQueryParams(params)}`),
  driverFreeWeeksPathById: (driverId, insuranceTypeId) => (
    `/api/Drivers/${driverId}/insurance/types/${insuranceTypeId}/freeWeeks`),
  driverDispatchDetailsGridPath: (params) => (
        `/api/Drivers/DispatchDetailsGrid${getQueryParams(params)}`),
  deleteFreeweeksPath: (driverId, freekWeeksId) => (
        `/api/Drivers/insurance/freeWeeks/${freekWeeksId}`),
  // Bulk Changes
  bulkStatusAssign: () => '/api/Drivers/bulkStatusAssign/assign',
  bulkStatusAssignValidate: () => '/api/Drivers/bulkStatusAssign/validate',
  // Others
  driversClaimsLookupPath: (params) => `/api/Drivers/claimsLookup${getQueryParams(params)}`,
  metricsPath: (params) => `/api/Drivers/metrics${getQueryParams(params)}`,
  driverInsuranceTypesPath: () => '/api/Drivers/insuranceTypes',
  driverInsuranceDefaultAmountPath: (driverId, insuranceTypeId) => `/api/Drivers/${driverId}/defaultInsuranceAmount/${insuranceTypeId}`,
  driverInsuranceDefaultFilePath: (driverId, insuranceTypeId) => `/api/Drivers/${driverId}/defaultInsuranceFile/${insuranceTypeId}`,
  dispatchingDriverPathById: (driverId) => `/dispatching/drivers/${driverId}`,
  driverInsuranceCancellationEmailPathByDriverId: (driverId) => `/api/Drivers/${driverId}/insuranceCancellationEmail`,
};

export const getEndorsementTypes = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/DriverEndorsementTypes`));
export const useGetEndorsementTypes = (opts = {}) => useQuery(['endorsementTypes'], getEndorsementTypes, { ...CacheConfig.forever, ...opts });

export const getDrivers = async (search) => {
  try {
    const response = await authFetch.get(
      `/api/Drivers/Grid?${search && Object.keys(search).length ? qs.stringify(search) : ''}`,
    );
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getDriversForExport = (params) => makeRequest(
  authFetch.get(routes.driversExportPath(params)),
);

export const getDriversLookup = (params) => makeRequest(
  authFetch.get(routes.driversLookupPath(params)),
);

export const getDriversLookupWithStatus = (params) => makeRequest(
  authFetch.get(`/api/Drivers/lookupWithStatus${getQueryParams(params)}`),
);

export const getDriversClaimsLookup = (params) => makeRequest(
  authFetch.get(routes.driversClaimsLookupPath(params)),
);

export const getDriverDQFStats = (id) => makeRequest(
  authFetch.get(`/api/Drivers/${id}/DriverDQFStats`),
);

export const getDriversSafetyGrid = (params) => makeRequest(
  authFetch.get(routes.safetyDriversGridPath(params)),
);

export const postDriver = (driver) => makeRequest(
  authFetch.put(routes.driversPath(), driver),
);

export const getDriverStats = (id, params) => makeRequest(authFetch.get(routes.driverStatsPath(id, params)));

export const getDriver = (driverNumber) => makeRequest(
  authFetch.get(`/api/Drivers/${driverNumber}`),
);

export const getDriverFiles = (id, params) => makeRequest(
  authFetch.get(routes.driverFilesPathByDriverId(id, params)),
);

export const getDriverNotes = (id, params) => makeRequest(
  authFetch.get(`${routes.driverNotesPathByDriverId(id, params)}`),
);

export const pinDriverNote = (driverId, noteId, pinState) => makeRequest(
  authFetch.patch(`${routes.driverNotesPinTogglePath(driverId, noteId)}`, pinState),
);

export const patchDriverNote = (id, note) => makeRequest(
  authFetch.patch(`${routes.driverNotesPathByDriverId(id)}`, note),
);

export const postDriverFiles = (id, files) => makeRequest(
  authFetch.post(routes.driverFilesPathByDriverId(id), files),
);

export const deleteDriverFile = (driverId, fileId) => makeRequest(
  authFetch.delete(routes.driverFilePath(driverId, fileId)),
);

export const deleteFreeWeeks = (driverId, freekWeeksId) => makeRequest(
  authFetch.delete(routes.deleteFreeweeksPath(driverId, freekWeeksId)),
);

export const putDriverFile = (driverId, fileId, file) => makeRequest(
  authFetch.put(routes.driverFilePath(driverId, fileId), file),
);

export const getDriverFileTypes = () => makeRequest(
  authFetch.get('/api/Drivers/FileTypes'),
);

export const getDriverMedicalFileTypes = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/FileTypes/Medical`));
export const useGetDriverMedicalFileTypes = (opts = {}) => useQuery(
  ['driverMedicalFileTypes'], getDriverMedicalFileTypes, { ...CacheConfig.forever, ...opts },
);

export const getDriverStatusTypes = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/Statuses/Types`));
export const useGetDriverStatusTypes = (opts = {}) => useQuery(['driverStatusTypes'], getDriverStatusTypes, { ...CacheConfig.forever, ...opts });

export const getDriverTypes = () => makeRequest(authFetch.get('/api/Drivers/Types'));
export const useGetDriverTypes = (opts = {}) => useQuery(['driverTypes'], getDriverTypes, { ...CacheConfig.forever, ...opts });

export const getDriverAudits = (id, params) => makeRequest(
  authFetch.get(`/api/Drivers/${id}/Audits${getQueryParams(params)}`),
);

export const getDriverAuditProperties = (id) => makeRequest(
  authFetch.get(`/api/Drivers/${id}/Audits/Properties`),
);

export const getDriverAuditUsers = (id) => makeRequest(
  authFetch.get(`/api/Drivers/${id}/Audits/Users`),
);

export const getDriverCertCategories = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/DriverLicenseCertificationEndorsementCategories`));
export const useGetDriverCertCategories = (opts = {}) => useQuery(
  ['driverCertCategories'], getDriverCertCategories, { ...CacheConfig.forever, ...opts },
);

export const getDriverCertTypes = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/DriverLicenseCertificationEndorsementTypes`));
export const useGetDriverCertTypes = (opts = {}) => useQuery(['driverCertTypes'], getDriverCertTypes, { ...CacheConfig.forever, ...opts });

export const getRestrictions = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/Restrictions`));
export const useGetRestrictions = (opts = {}) => useQuery(['restrictions'], getRestrictions, { ...CacheConfig.forever, ...opts });

export const getMedicalRestrictions = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/MedicalRestrictions`));
export const useGetMedicalRestrictions = (opts = {}) => useQuery(
  ['medicalRestrictions'], getMedicalRestrictions, { ...CacheConfig.forever, ...opts },
);

export const getDriverCertifications = (driverId) => authFetch
  .get(`/api/Drivers/${driverId}/LicenseCertificationEndorsements`)
  .catch((error) => {
    console.log(error);
    throw error;
  });

export const postDriverCertifications = (data) => makeRequest(
  authFetch.post('/api/Drivers/LicenseCertificationEndorsements', data),
);

export const getComplianceCheck = (driverId) => makeRequest(
  authFetch.get(`/api/Drivers/${driverId}/ComplianceCheck`),
);

export const getDriverInServicePrevalidation = (driverId) => makeRequest(
  authFetch.get(`/api/Drivers/${driverId}/PrevalidateInService`),
);

export const getSafetyInfo = (driverId) => authFetch
  .get(`/api/Drivers/${driverId}/driverSafetyInfo`)
  .catch((error) => {
    console.log(error);
    throw error;
  });

export const upsertSafetyInfo = (driverId, data) => makeRequest(
  authFetch.put(`/api/Drivers/${driverId}/driverSafetyInfo`, data),
);

export const getFuelTypes = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/FuelTypes`));
export const useGetFuelTypes = (opts = {}) => useQuery(['fuelTypes'], getFuelTypes, { ...CacheConfig.forever, ...opts });

export const getDistanceCategories = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/DistanceCategories`));
export const useGetDistanceCategories = (opts = {}) => useQuery(['distance-categories'], getDistanceCategories, { ...CacheConfig.forever, ...opts });

export const getDriverMessagingPreferences = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/MessagingPreferences`));
export const useGetDriverMessagingPreferences = (opts = {}) => useQuery(
  ['driver-messaging-preferences'], getDriverMessagingPreferences, { ...CacheConfig.forever, ...opts },
);

export const getCountries = () => makeRequest(authFetch.get(`${DRIVERS_PATH}/countries`));
export const useGetCountries = (opts = {}) => useQuery(['countries'], getCountries, { ...CacheConfig.forever, ...opts });

export const getCorrectiveActionsByDriverId = (driverId) => authFetch
  .get(routes.correctiveActionsPathByDriverId(driverId))
  .catch((error) => {
    console.log(error);
    throw error;
  });

export const postCorrectiveAction = (driverId, data) => makeRequest(
  authFetch.patch(routes.correctiveActionsPathByDriverId(driverId), data),
);

export const getCorrectiveActionById = (driverId, id) => makeRequest(
  authFetch.get(routes.correctiveActionPathById(driverId, id)),
);

export const getDriverAssignment = (driverId) => makeRequest(
  authFetch.get(`/api/Drivers/${driverId}/unit`),
);

export const deleteDriverAssignment = (driverId, options) => makeRequest(
  authFetch.delete(`/api/Drivers/${driverId}/unit`, options),
);

export const getDriverAssignmentsHistory = (driverId) => makeRequest(authFetch.get(`/api/Drivers/${driverId}/unit/history`));

export const getDriverAssignments = (driverId, params) => makeRequest(
  authFetch.get(`/api/Units/trucks${getQueryParams({ ...params, PreviouslyAssignedToId: driverId })}`),
);

export const bulkStatusAssignValidate = (data) => makeRequest(
  authFetch.patch(routes.bulkStatusAssignValidate(), data),
);
export const changeAllValidate = (data) => makeRequest(authFetch.patch(routes.changeAllValidate(), data));
export const changeAllAssign = (data) => makeRequest(authFetch.patch(routes.changeAllAssign(), data));

export const bulkStatusAssign = (data) => makeRequest(
  authFetch.patch(routes.bulkStatusAssign(), data),
);

export const getSafetyMetrics = (params) => makeRequest(
  authFetch.get(routes.metricsPath(params)),
);

export const getDriverBanks = (id) => makeRequest(
  authFetch.get(routes.driverBanksPath(id)),
);

export const setDriverBanks = (id, data) => makeRequest(
  authFetch.patch(routes.driverBanksPath(id), data),
);

export const revealDriverAccountInfo = (driverId, accountId) => makeRequest(
  authFetch.get(routes.driverAccountInfoPath(driverId, accountId)),
);

export const getDriverInsuranceTypes = (params) => makeRequest(
  authFetch.get(routes.driverInsuranceTypesPath(), { params }),
);

export const getDefaultInsuranceAmount = (driverId, insuranceTypeId) => makeRequest(
  authFetch.get(routes.driverInsuranceDefaultAmountPath(driverId, insuranceTypeId)),
);

export const getDriverPastInsuranceHistory = (driverId, insuranceId, params) => makeRequest(
  authFetch.get(routes.driverPastInsurancesPathByIds(driverId, insuranceId, params)),
);

export const getDriverPastInsuranceFreeWeeks = (driverId, insuranceType, params) => makeRequest(
  authFetch.get(routes.driverFreeWeeksPathGridById(driverId, insuranceType, params)),
);

export const postDriverPastInsuranceFreeWeeks = (driverId, insuranceType, data) => makeRequest(
  authFetch.post(routes.driverFreeWeeksPathById(driverId, insuranceType), data),
);

export const getDriverEditableInsuranceHistory = (driverId, insuranceId, params) => makeRequest(
  authFetch.get(routes.driverFutureInsurancesPathByIds(driverId, insuranceId, params)),
);

export const putDriverInsurance = (driverId, insuranceId, data) => makeRequest(
  authFetch.put(routes.driverInsurancePathByIds(driverId, insuranceId), data),
);

export const postNewDriverInsurance = (driverId, data) => makeRequest(
  authFetch.post(routes.driverInsurancePathByDriverId(driverId), data),
);

export const getDriverCurrentInsurance = (driverId) => makeRequest(
  authFetch.get(routes.driverInsurancePathByDriverId(driverId)),
);

export const deleteDriverInsurance = (driverId, insuranceId) => makeRequest(
  authFetch.delete(routes.driverInsurancePathByIds(driverId, insuranceId)),
);

export const getInsuranceEmailOptions = (driverId) => makeRequest(
  authFetch.get(routes.driverInsuranceEmailPathByDriverId(driverId)),
);

export const patchInsuranceEmail = (driverId) => makeRequest(
  authFetch.patch(routes.driverInsuranceEmailPathByDriverId(driverId)),
);

export const patchInsuranceCancellationEmail = (driverId) => makeRequest(
  authFetch.patch(routes.driverInsuranceCancellationEmailPathByDriverId(driverId)),
);

export const getDriverDispatchDetailsGrid = (params) => makeRequest(
  authFetch.get(routes.driverDispatchDetailsGridPath(params)),
);

export const getDriverInsuranceFile = (driverId, insuranceTypeId) => makeRequest(
  authFetch.get(routes.driverInsuranceDefaultFilePath(driverId, insuranceTypeId)),
);

export const getBulkDqfFiles = (params) => authFetch
  .patch(routes.bulkDqfDownload(), params, {
    responseType: 'blob',
  })
  .then((resp) => resp)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const getDqfFiles = (id) => authFetch
  .get(routes.dqfDownload(id), {
    responseType: 'blob',
  })
  .then((resp) => resp)
  .catch((error) => {
    console.error(error);
    throw error;
  });
